/**
 * SB5-B.2 — TanStack Query hooks for the contract-positions feature.
 *
 * Covers all 11 BE endpoints from projects/v1@2.1.0:
 *   - list, facets/{projects,sites,assets,states}
 *   - detail
 *   - update (PATCH), withdraw, reject, create (POST)
 *
 * Plus two helpers for the Add modal cascade:
 *   - contracts list filtered by projectId (existing /contracts endpoint)
 *   - assets list filtered by siteId (existing /turbines endpoint —
 *     the FE-side "asset" is currently a Turbine; per spec the Add
 *     modal picks an asset from the contract's site)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectsFetch } from "../projects/projectsFetch.js";
import { assetsFetch } from "../sites/queries/assetsFetch.js";
import { contractPositionsKeys } from "./keys.js";
import { contractsKeys } from "../contracts/keys.js";
import type { ContractListResponse } from "../contracts/types.js";
import type {
  ContractPositionCreateRequest,
  ContractPositionDetail,
  ContractPositionListResponse,
  ContractPositionUpdateRequest,
  ContractPositionsFacetResponse,
  ListContractPositionsParams,
} from "./types.js";

function listContractPositionsUrl(params: ListContractPositionsParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 50));
  if (params.q && params.q.length > 0) sp.set("q", params.q);
  if (params.projectIds && params.projectIds.length > 0) {
    sp.set("projectIds", params.projectIds.join(","));
  }
  if (params.siteIds && params.siteIds.length > 0) {
    sp.set("siteIds", params.siteIds.join(","));
  }
  if (params.assetIds && params.assetIds.length > 0) {
    sp.set("assetIds", params.assetIds.join(","));
  }
  if (params.states && params.states.length > 0) {
    sp.set("states", params.states.join(","));
  }
  sp.set("includeCompleted", params.includeCompleted ? "true" : "false");
  if (params.sortBy) sp.set("sortBy", params.sortBy);
  if (params.sortDir) sp.set("sortDir", params.sortDir);
  // TNCY-8 (projects/v1@2.6.0): scope=member fans the list out across the
  // caller's granted cross-tenant contracts' positions. Only sent when
  // explicitly `member` — omitting it leaves the BE on its `own` default
  // (no wire churn for own-only callers).
  if (params.scope === "member") sp.set("scope", "member");
  return `/contract-positions?${sp.toString()}`;
}

export function useContractPositionsListQuery(
  params: ListContractPositionsParams,
  // FB-01: optional `enabled` gate so tenant-scoped callers can defer the
  // fetch until session+tenant resolve (avoids the missing-X-Tenant-ID 400
  // race — `reference_tenant_scoped_query_enabled_gate`). Defaults to `true`
  // so existing callers are unaffected.
  options?: { enabled?: boolean },
) {
  return useQuery<ContractPositionListResponse>({
    queryKey: contractPositionsKeys.list(params),
    queryFn: () =>
      projectsFetch<ContractPositionListResponse>(
        listContractPositionsUrl(params),
      ),
    staleTime: 30_000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
}

export function useContractPositionsProjectsFacetQuery() {
  return useQuery<ContractPositionsFacetResponse>({
    queryKey: contractPositionsKeys.projectsFacet(),
    queryFn: () =>
      projectsFetch<ContractPositionsFacetResponse>(
        "/contract-positions/facets/projects",
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Typeahead sites facet. Per the BE spec — empty `q` returns no
 * options (the facet is intentionally typeahead-only).
 */
export function useContractPositionsSitesFacetQuery(q: string) {
  return useQuery<ContractPositionsFacetResponse>({
    queryKey: contractPositionsKeys.sitesFacet(q),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q.length > 0) sp.set("q", q);
      return projectsFetch<ContractPositionsFacetResponse>(
        `/contract-positions/facets/sites${sp.toString() ? `?${sp.toString()}` : ""}`,
      );
    },
    staleTime: 30_000,
    retry: false,
    enabled: q.trim().length > 0,
  });
}

export function useContractPositionsAssetsFacetQuery(q: string) {
  return useQuery<ContractPositionsFacetResponse>({
    queryKey: contractPositionsKeys.assetsFacet(q),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q.length > 0) sp.set("q", q);
      return projectsFetch<ContractPositionsFacetResponse>(
        `/contract-positions/facets/assets${sp.toString() ? `?${sp.toString()}` : ""}`,
      );
    },
    staleTime: 30_000,
    retry: false,
    enabled: q.trim().length > 0,
  });
}

export function useContractPositionsStatesFacetQuery() {
  return useQuery<ContractPositionsFacetResponse>({
    queryKey: contractPositionsKeys.statesFacet(),
    queryFn: () =>
      projectsFetch<ContractPositionsFacetResponse>(
        "/contract-positions/facets/states",
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useContractPositionDetailQuery(id: string | null) {
  return useQuery<ContractPositionDetail>({
    queryKey: contractPositionsKeys.detail(id ?? "__none__"),
    queryFn: () =>
      projectsFetch<ContractPositionDetail>(`/contract-positions/${id ?? ""}`),
    staleTime: 30_000,
    retry: false,
    enabled: typeof id === "string" && id.length > 0,
  });
}

export function useUpdateContractPositionMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractPositionUpdateRequest) =>
      projectsFetch<ContractPositionDetail>(`/contract-positions/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: contractPositionsKeys.detail(id),
      });
    },
  });
}

export function useWithdrawContractPositionMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      projectsFetch<ContractPositionDetail>(
        `/contract-positions/${id}/withdraw`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: contractPositionsKeys.detail(id),
      });
    },
  });
}

export function useRejectContractPositionMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      projectsFetch<ContractPositionDetail>(
        `/contract-positions/${id}/reject`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: contractPositionsKeys.detail(id),
      });
    },
  });
}

export function useCreateContractPositionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractPositionCreateRequest) =>
      projectsFetch<ContractPositionDetail>("/contract-positions", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
    },
  });
}

/**
 * PMGT (projects/v1@2.12.0) — `DELETE /contract-positions/{id}`
 * (`deleteContractPosition`). Soft-deletes a turbine (the project-edit
 * "remove turbine" action). The BE returns **409**
 * `{ code: conflict, reason: inspection_instruction_linked }` when an active
 * inspection instruction references the position — the caller surfaces a
 * friendly "can't remove — an inspection instruction is linked" message from
 * the thrown `ProjectsFetchError`. On success (204) it invalidates the
 * positions lists so the tree/map drop the removed turbine.
 */
export function useDeleteContractPositionMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (contractPositionId) =>
      projectsFetch<undefined>(
        `/contract-positions/${encodeURIComponent(contractPositionId)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
    },
  });
}

// ---- Add-modal helpers ------------------------------------------------

/**
 * Contracts in a given project — drives the second picker in the
 * Add-Contract-Position cascade. Reuses the existing /contracts list
 * endpoint with `projectIds` narrowing.
 */
export function useContractsByProjectQuery(projectId: string | null) {
  // Build a thin URL — only set projectIds when present so the query
  // never sends an empty filter.
  function url(): string {
    const sp = new URLSearchParams();
    sp.set("page", "1");
    sp.set("pageSize", "200");
    sp.set("includeCompleted", "false");
    if (projectId) sp.set("projectIds", projectId);
    return `/contracts?${sp.toString()}`;
  }
  return useQuery<ContractListResponse>({
    queryKey: [...contractPositionsKeys.contractsByProject(projectId)],
    queryFn: () => projectsFetch<ContractListResponse>(url()),
    staleTime: 30_000,
    retry: false,
    enabled: typeof projectId === "string" && projectId.length > 0,
  });
}

/**
 * Assets in a given site — drives the third picker in the Add-Contract-
 * Position cascade. The "asset" record on this codebase today is a
 * Turbine row from `assets/v1@1.1.0` — see
 * `apps/web/src/features/turbines/`. We reuse `GET /api/assets/v1/turbines?site=<siteId>`
 * and the response shape `ListTurbinesResponse` (we only read
 * `assetId/displayLabel` worth of fields).
 *
 * Per Q17 — do NOT filter out already-linked assets; the UNIQUE
 * constraint was dropped BE-side in migration 032 so re-inspecting
 * the same asset twice in one contract is valid.
 */
interface AssetsBySiteRow {
  /** `companies_db.tenant_*.assets.asset_id` (UUID). */
  id: string;
  assetCode: number;
  internalParkNumber: string | null;
  identifier: string | null;
  modelName: string;
  siteId: string;
  siteName: string;
}

interface AssetsBySiteResponse {
  items: AssetsBySiteRow[];
  total: number;
}

/**
 * Compose the same `assetDisplayLabel` shape the BE emits on the
 * Contract Positions detail row. We don't have
 * `manufacturerAbbreviation` on the Turbine summary today; the closest
 * is the model name. The Add modal preview just needs a stable
 * "${manufacturerAbbreviation} ${identifier} (${internalParkNumber})"
 * with graceful fallbacks per spec story 03 §1.
 */
export function composeAssetLabel(row: AssetsBySiteRow): string {
  const head = row.identifier?.trim() ?? String(row.assetCode);
  const tail =
    row.internalParkNumber && row.internalParkNumber.trim().length > 0
      ? ` (${row.internalParkNumber})`
      : "";
  return `${head}${tail}`.trim();
}

export function useAssetsBySiteQuery(siteId: string | null) {
  return useQuery<AssetsBySiteResponse>({
    queryKey: [...contractPositionsKeys.assetsBySite(siteId)],
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set("page", "1");
      sp.set("pageSize", "200");
      if (siteId) sp.set("site", siteId);
      return assetsFetch<AssetsBySiteResponse>(`/turbines?${sp.toString()}`);
    },
    staleTime: 30_000,
    retry: false,
    enabled: typeof siteId === "string" && siteId.length > 0,
  });
}

// Re-export contractsKeys / contractsByProject in case PR-B tests need
// to invalidate from outside (currently unused but kept for symmetry).
export { contractsKeys };

export type { AssetsBySiteResponse, AssetsBySiteRow };
